<template>
  <div :class="$style['rail']">
    <div v-if="$slots.title || scrollable" :class="$style['rail__head']">
      <slot name="title" />
      <!-- With a mouse there is no swipe: the arrows scroll a screen's width -->
      <div v-if="scrollable" :class="$style['rail__controls']">
        <Button
          variant="outline"
          icon-only
          round
          icon="ChevronLeft"
          aria-label="Vorige"
          :disabled="atStart"
          @click="page(-1)" />
        <Button
          variant="outline"
          icon-only
          round
          icon="ChevronRight"
          aria-label="Volgende"
          :disabled="atEnd"
          @click="page(1)" />
      </div>
    </div>
    <ul ref="track" :class="$style['rail__track']" :aria-label="label" @scroll.passive="update">
      <li v-for="(item, index) in items" :key="keyOf(item, index)" :class="$style['rail__item']">
        <slot name="item" :item="item" :index="index" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts" generic="T">
// A row you scroll sideways: swipe on a phone, arrows with a mouse. It runs to the edges of the screen,
// so the next item peeks out, and lines up with the page at the start.
const props = withDefaults(defineProps<{
  items: T[]
  label: string
  itemKey?: string
}>(), { itemKey: 'id' })

const track = ref<HTMLElement>()
const atStart = ref(true)
const atEnd = ref(false)
const scrollable = ref(false)
let observer: ResizeObserver | undefined

const keyOf = (item: T, index: number) => ((item as Record<string, unknown>)?.[props.itemKey] as PropertyKey | undefined) ?? index

function update () {
  const el = track.value
  if (!el) { return }
  scrollable.value = el.scrollWidth > el.clientWidth + 1
  atStart.value = el.scrollLeft <= 1
  atEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

function page (direction: 1 | -1) {
  const el = track.value
  if (!el) { return }
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: smooth ? 'smooth' : 'instant' })
}

onMounted(() => {
  update()
  observer = new ResizeObserver(update)
  if (track.value) { observer.observe(track.value) }
})

watch(() => props.items.length, () => nextTick(update))
onBeforeUnmount(() => observer?.disconnect())
</script>

<style module>
.rail {
  --rail-gap: var(--spacing-xl);
  --rail-gutter: var(--container-gutter, var(--spacing-xl));
  --rail-columns: 6;

  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  min-width: 0;
}

.rail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-l);
}

.rail__head > :first-child {
  margin: 0;
}

.rail__controls {
  display: flex;
  flex: none;
  gap: var(--spacing);
  margin-left: auto;
}

/* Out of the way at the end, but in the same place, so the other one doesn't move */
.rail__controls [disabled] {
  opacity: 0.3;
}

.rail__track {
  display: grid;
  grid-auto-columns: calc((100% - (var(--rail-columns) - 1) * var(--rail-gap)) / var(--rail-columns));
  grid-auto-flow: column;
  gap: var(--rail-gap);
  margin: 0 calc(-1 * var(--rail-gutter));
  padding: 0 var(--rail-gutter);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  list-style: none;
  scroll-padding-inline: var(--rail-gutter);
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}

.rail__track::-webkit-scrollbar {
  display: none;
}

.rail__item {
  min-width: 0;
  scroll-snap-align: start;
}

@media (width <= 80rem) {
  .rail {
    --rail-columns: 5;
  }
}

@media (width <= 64rem) {
  .rail {
    --rail-columns: 4;
  }
}

/* A phone: two and a bit, so you see there is more */
@media (width <= 40rem) {
  .rail {
    --rail-gap: var(--spacing-l);
    --rail-columns: 2.3;
  }
}

@media (hover: none) {
  .rail__controls {
    display: none;
  }
}
</style>
